import { zxcvbn } from '@zxcvbn-ts/core';
import { rem } from 'polished';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import * as yup from 'yup';

import { useABLincoln } from '@vimeo/ablincoln';
import { Box, InputGroup, InputRightElement, Collapse, Text } from '@vimeo/bokeh';
import { EyeShut } from '@vimeo/bokeh-icons';
import { useField, useForm } from '@vimeo/forms';
import { CONTROLLER } from '@vimeo/forms/useForm';
import { translate } from '@vimeo/i18n-runtime';
import { Paragraph } from '@vimeo/iris/typography';
import { useViewer } from '@vimeo/viewer';

import { useRecaptchaEnterpriseToken } from '../../../F2PJoinLogin/useRecaptchaEnterpriseToken';
import { InputWrapper, Label, StyledInput } from '../../../InputPlaceholderLabel';
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '../../constants';
import {
  trackJoinPageClickRegFlow0625,
  trackJoinPageImpressionRegFlow0625,
  trackJoinWithEmailClick,
  trackRegistration,
  trackFinishAuthFlow,
  BpData,
} from '../../tracking';
import { AgeCertificationCheckbox } from '../AgeCertificationCheckbox';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { Login } from './RequestLogin';
import { ContinueButton, Form, FormSection, Input, Notice } from './Shared';
import { ErrorCheck } from './types';

interface Props {
  xsrft: string;
  optIn: boolean;
  redirectUrl?: string;
  name?: string;
  email?: string;
  disableEmail?: boolean;
  fa?: {
    container: string;
    thirdPartyIntegration?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (response: any) => void;
  onEmailChange?: (email: string) => void;
  formType?: 'underline' | 'minimal';
  inputFieldVariant?: 'underline';
  submitCtaText?: string;
  pill?: boolean;
  emailRef?: React.RefObject<HTMLInputElement>;
  source?: string;
  termsAndConditions?: string;
  disclaimer?: string;
  placeholder?: {
    email: string;
    name?: string;
    password: string;
    confirmPassword?: string;
  };
  inputFieldsOrder?: {
    email: number;
    name: number;
    password: number;
    confirmPassword?: number;
  };
  hasF2PVariant?: boolean;
  marketingCheckBox?: JSX.Element;
  turnstileToken?: string | undefined;
  hideEmail?: boolean;
  hideName?: boolean;
  className?: string;
  isInGIPTest?: boolean;
  onTurnstileTokenChange?: Dispatch<SetStateAction<string | undefined>>;
  setIsCheckEmailVerifyScreen?: Dispatch<SetStateAction<boolean>>;
  setIsJoinSecondScreen?: Dispatch<SetStateAction<boolean>>;
  turnstileError?: string;
  turnstileSiteKey?: string;
  captchaVisible?: boolean;
  renderChallenge?: boolean;
  locale?: string;
  bpData?: BpData;
  isFullScreen?: boolean;
}

export function JoinEmailForm({
  xsrft,
  optIn,
  name,
  email,
  redirectUrl,
  fa,
  onSuccess,
  onEmailChange,
  disableEmail,
  inputFieldVariant,
  emailRef,
  placeholder,
  formType = 'minimal',
  submitCtaText = translate('Join with email'),
  pill = false,
  inputFieldsOrder,
  source,
  termsAndConditions,
  disclaimer,
  hasF2PVariant,
  marketingCheckBox,
  className,
  hideEmail = false,
  hideName = false,
  isInGIPTest = false,
  turnstileToken,
  turnstileError,
  captchaVisible,
  setIsJoinSecondScreen,
  setIsCheckEmailVerifyScreen,
  bpData,
  isFullScreen = false,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<Record<string, unknown>>({});
  const [hideConfirmPassword, setHideConfirmPassword] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [ageCertificationInvalid, setAgeCertificationInvalid] = useState<boolean>(false);

  const isRecaptchaEnabledForSignup =
    useABLincoln()(
      'JS.TrustAndSafety.RecaptchaEnterprise.Signup',
      'enable_recaptcha_enterprise_signup',
      'control',
    ) === 'variant';
  const recaptchaEnterpriseToken = useRecaptchaEnterpriseToken(isRecaptchaEnabledForSignup);

  const viewer = useViewer();

  const shouldShowAgeCertification = Boolean(viewer?.requiresAgeSelfCertification);

  useEffect(() => {
    if (turnstileError) {
      setError(turnstileError);
    }
  }, [turnstileError]);

  useEffect(() => {
    trackJoinPageImpressionRegFlow0625({
      location: 'join_page_step_2',
    });
  }, []);

  const newRegFlowVariantInitialValues = isFullScreen
    ? {
        confirmPassword: '',
      }
    : {};
  const newRegFlowVariantValidation = isFullScreen
    ? {
        confirmPassword: yup
          .string()
          .oneOf([yup.ref('password')], translate('Please confirm password'))
          .required(translate('Please confirm password')),
      }
    : {};

  const passwordError = translate({
    singular:
      'Password must be at least {MIN} characters and contain at least one number and at least one symbol.',
    replacements: {
      MIN: MIN_PASSWORD_LENGTH,
    },
  });
  const validationSchema = yup.object({
    name: yup.string().max(32).required(translate('Please enter your name')),
    email: yup
      .string()
      .email(translate('Please enter a valid email address'))
      .required(translate('Please enter a valid email address')),
    password: yup
      .string()
      .min(MIN_PASSWORD_LENGTH, passwordError)
      .matches(/[0-9]/, passwordError)
      .matches(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/, passwordError)
      .max(
        MAX_PASSWORD_LENGTH,
        translate({
          singular:
            'Password must be less than {MAX} characters and contain at least one number and at least one symbol.',
          replacements: {
            MAX: MAX_PASSWORD_LENGTH,
          },
        }),
      )
      .required(translate('Please enter your password')),

    ...newRegFlowVariantValidation,
  });
  const emailForm = useForm({
    validationSchema: validationSchema,
    initialValues: {
      email: email || '',
      password: '',
      name: name || '',
      overEighteenCertification: false,
      ...newRegFlowVariantInitialValues,
    },
    onSubmit: async (values) => {
      const errors = emailForm[CONTROLLER]?.state?.errors;
      const errorChecks: ErrorCheck[] = [
        {
          field: 'email',
          errorCode: { has_error_invalid_email: '' },
        },
        {
          field: 'password',
          errorCode: { has_error_password_too_simple: '' },
        },
      ];

      for (const { field, errorCode } of errorChecks) {
        if (
          errors?.has(field) &&
          Array.isArray(errors.get(field)) &&
          (errors.get(field)?.length ?? 0) > 0
        ) {
          setError(errors.get(field)?.[0] ?? '');
          setErrorCode(errorCode);
          return;
        }
      }

      if (shouldShowAgeCertification && !values.overEighteenCertification) {
        setError(translate('Please certify you are over 18 by checking input box'));
        setAgeCertificationInvalid(true);
        return;
      }

      if (isFullScreen && values.password !== values.confirmPassword) {
        setError(translate('Please confirm password'));
        setErrorCode({ has_error_password_missmatch: '' });
        return;
      }

      setShowPassword(false);
      let data =
        `name=${values.name}&` +
        `email=${encodeURIComponent(values.email)}&` +
        `password=${encodeURIComponent(values.password)}&` +
        `token=${xsrft}&` +
        `action=join&` +
        `service=${isInGIPTest ? 'google_identity_platform' : 'vimeo'}&` +
        `marketing_opt_in=${optIn}&` +
        `recaptcha_enterprise_token=${recaptchaEnterpriseToken}&` +
        `turnstile_token=${turnstileToken}&` +
        `self_certified_over_eighteen=${values.overEighteenCertification}`;

      /*
       * CI Branch Fix: This `redirect` query param is needed for, similar check is already
       * added for backend driven login screen, check AuthGrantController::buildAuthFormRedirectParams
       * for more details.
       */
      if (redirectUrl) {
        data += `&redirect=${encodeURIComponent(redirectUrl)}`;
      }

      if (source) {
        data += `&source=${encodeURIComponent(source)}`;
      }

      let hasGIPFailed = false;

      if (isInGIPTest) {
        const module = await import('@vimeo/google-identity-platform');
        const {
          ERROR_CODES_TO_LOG,
          SIGNUP_ERROR_EMAIL_EXISTS,
          SIGNUP_ERROR_INVALID_EMAIL,
          SIGNUP_ERROR_WEAK_PASSWORD,
          GoogleIdentityPlatform,
        } = module;
        const googleIdentityPlatform = new GoogleIdentityPlatform();
        try {
          if (values.email && values.password) {
            let response = await googleIdentityPlatform.signUpWithEmailAndPassword(
              values.email,
              values.password,
            );
            data += `&gip_id_token=${response._tokenResponse.idToken}`;
            data += `&gip_uid=${response.user.uid}`;
          }
        } catch (error) {
          hasGIPFailed = true;
          const errorCode = error.code;
          let errorMsg = translate(
            'Whoops! We are having some technical difficulties, please try again in a minute.',
          );

          if (errorCode === SIGNUP_ERROR_EMAIL_EXISTS) {
            errorMsg = translate('There is already a Vimeo user with this email address.');
          }

          if (errorCode === SIGNUP_ERROR_WEAK_PASSWORD) {
            errorMsg = translate('The password is too weak.');
          }

          if (errorCode == SIGNUP_ERROR_INVALID_EMAIL) {
            errorMsg = translate('Please enter a valid email address.');
          }

          setError(errorMsg);
          setErrorCode(errorCode);

          if (ERROR_CODES_TO_LOG.includes(errorCode)) {
            // TODO: add logging here for select error codes?
          }
        }
      }

      if (!hasGIPFailed) {
        try {
          const response = await Login(data, '/join');
          if (onSuccess) {
            onSuccess(response);
          }
        } catch (errors) {
          let msg = translate(
            'Whoops! We are having some technical difficulties, please try again in a minute.',
          );

          for (const key in errors) {
            // Ignore errors with 'has_error_' prefix. These are used for client-side
            // error handling and do not point to actual error strings.
            if (errors.hasOwnProperty(key) && key.indexOf('has_error_') !== 0) {
              msg = errors[key];
              break;
            }
          }
          if (
            (hasF2PVariant &&
              !errors.hasOwnProperty('has_error_invalid_email') &&
              errors.hasOwnProperty('email')) ||
            errors.hasOwnProperty('new_password') ||
            errors.hasOwnProperty('display_name')
          ) {
            msg = translate('Please enter your name, email, and password');
          }

          if (errors.has_error_user_exists) {
            msg = translate('There is already a Vimeo user with this email address.');
          }

          if (errors.hasOwnProperty('has_captcha_error')) {
            msg = translate({
              singular:
                'Unable to verify CAPTCHA. Please try again or visit the {A}Support Center{/A} for help.',
              replacements: {
                A: (children: string) => (
                  <a href="https://vimeo.com/help/contact" target="_blank" rel="noreferrer">
                    {children}
                  </a>
                ),
              },
            });
          }

          setError(msg);
          setErrorCode(errors);
          if (errors.hasOwnProperty('email_verification_required')) {
            setError(null);
            if (setIsJoinSecondScreen && setIsCheckEmailVerifyScreen) {
              setIsJoinSecondScreen(false);
              setIsCheckEmailVerifyScreen(true);
            }
            return;
          }
        }
      }
    },
  });

  const userName = useField(emailForm, 'name');
  const userEmail = useField(emailForm, 'email');
  const password = useField(emailForm, 'password');
  const confirmPassword = useField(emailForm, 'confirmPassword');
  const overEighteenField = useField(emailForm, 'overEighteenCertification');

  const onChangeEmailField = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    userEmail.iris.onChange(changeEvent);
    setError(null);
    onEmailChange && onEmailChange(changeEvent.target.value);
  };
  const inputNameText = placeholder
    ? { placeholder: placeholder.name }
    : { label: translate('Name') };
  const inputEmailText = placeholder
    ? { placeholder: placeholder.email }
    : { label: translate('Email') };
  const inputPasswordText = placeholder
    ? { placeholder: placeholder.password }
    : { label: translate('Password') };

  const hideErrorBox =
    isFullScreen &&
    (errorCode.hasOwnProperty('has_error_password_missmatch') ||
      errorCode.hasOwnProperty('has_error_user_exists') ||
      errorCode.hasOwnProperty('has_error_invalid_email'));
  const eyeShutIconProps = {
    _hover: { cursor: 'pointer' },
    color: 'text-secondary',
  };

  const passwordScore = password.iris.value ? zxcvbn(password.iris.value).score : undefined;

  return (
    <Form
      onSubmit={emailForm.handleSubmit}
      noValidate
      style={{ gap: isFullScreen ? '12px' : '' }}
      onChange={() => {
        if (isFullScreen) {
          setErrorCode({});
          setError(null);
        }
      }}
    >
      {isFullScreen ? (
        <>
          {hideEmail ? null : (
            <InputWrapper
              formFieldErrorMessage={error}
              isInvalid={
                errorCode.hasOwnProperty('has_error_user_exists') ||
                errorCode.hasOwnProperty('has_error_invalid_email')
              }
            >
              <StyledInput
                isRequired
                order={inputFieldsOrder?.email}
                id="email_login"
                {...userEmail.iris}
                isDisabled={disableEmail}
                onChange={onChangeEmailField}
              />
              <Label>{inputEmailText.placeholder}</Label>
            </InputWrapper>
          )}
          {hideName ? null : (
            <InputWrapper>
              <StyledInput
                isRequired
                order={inputFieldsOrder?.name}
                id="name"
                type="text"
                {...userName.iris}
                autoFocus
              />
              <Label>{inputNameText.placeholder}</Label>
            </InputWrapper>
          )}
          <InputWrapper
            formFieldErrorMessage={error}
            isInvalid={errorCode.hasOwnProperty('has_error_password_missmatch')}
          >
            <InputGroup>
              <StyledInput
                isRequired
                order={inputFieldsOrder?.password}
                id="password_login"
                type={showPassword ? 'text' : 'password'}
                {...password.iris}
                onChange={(e) => {
                  password.iris.onChange(e);
                  setHideConfirmPassword(!e.target.value);
                }}
              />
              <Label>{inputPasswordText.placeholder}</Label>
              <InputRightElement>
                <EyeShut {...eyeShutIconProps} onClick={() => setShowPassword(!showPassword)} />
              </InputRightElement>
            </InputGroup>
          </InputWrapper>
          <Collapse
            style={{ overflow: 'visible' }}
            enterDuration="2xl"
            exitDuration="2xl"
            in={!hideConfirmPassword && isFullScreen}
            unmountOnExit
          >
            <InputWrapper>
              <InputGroup>
                <StyledInput
                  isRequired
                  order={inputFieldsOrder?.confirmPassword}
                  id="confirm_password_login"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...confirmPassword.iris}
                />
                <Label>{translate('Confirm password')}</Label>
                <InputRightElement>
                  <EyeShut
                    {...eyeShutIconProps}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                </InputRightElement>
              </InputGroup>
            </InputWrapper>
            <Box marginTop={rem(12)}>
              <PasswordStrengthIndicator passwordScore={passwordScore} />
            </Box>
            <Box marginTop={rem(12)}>
              <Text variant="body-sm" color="text-secondary">
                {passwordError}
              </Text>
            </Box>
          </Collapse>
          {error && !hideErrorBox && (
            <FormSection>
              <Notice format="negative">
                <Paragraph size="3">{error}</Paragraph>
              </Notice>
            </FormSection>
          )}
          {!hasF2PVariant && (
            <Box as="section" mx="auto" my={rem(10)} display={captchaVisible ? 'block' : 'none'}>
              <div id="turnstile-container"></div>
            </Box>
          )}
          {hasF2PVariant && (
            <>
              {marketingCheckBox}

              <AgeCertificationCheckbox
                overEighteenCertification={overEighteenField.input.value}
                setOverEighteenCertification={overEighteenField.handlers.setValue}
                setError={setError}
                isFullScreen={isFullScreen}
                shouldShowAgeCertification={shouldShowAgeCertification}
                isInvalid={ageCertificationInvalid}
                setAgeCertificationInvalid={setAgeCertificationInvalid}
              />

              {termsAndConditions && (
                <FormSection className="termsandconditions">
                  <Text variant="body-sm" color="text-secondary">
                    {termsAndConditions}
                  </Text>
                  {disclaimer && (
                    <Text variant="body-sm" color="text-secondary">
                      {disclaimer}
                    </Text>
                  )}
                </FormSection>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {hideName ? null : (
            <Input
              inputFieldVariant={inputFieldVariant}
              formType={formType}
              order={inputFieldsOrder?.name}
              {...inputNameText}
              id="name"
              type="text"
              {...userName.iris}
            />
          )}
          {hideEmail ? null : (
            <Input
              inputFieldVariant={inputFieldVariant}
              emailRef={emailRef}
              formType={formType}
              order={inputFieldsOrder?.email}
              {...inputEmailText}
              id="email_login"
              {...userEmail.iris}
              disabled={disableEmail}
              onChange={onChangeEmailField}
            />
          )}
          {/* #iris TODO convert to iris password input  */}
          <Input
            inputFieldVariant={inputFieldVariant}
            formType={formType}
            order={inputFieldsOrder?.password}
            id="password_login"
            {...inputPasswordText}
            type="password"
            {...password.iris}
          />
          {error && (
            <FormSection>
              <Notice format="negative">
                <Paragraph size="3">{error}</Paragraph>
              </Notice>
            </FormSection>
          )}
          {!hasF2PVariant && (
            <Box as="section" mx="auto" my={rem(10)} display={captchaVisible ? 'block' : 'none'}>
              <div id="turnstile-container"></div>
            </Box>
          )}
          {hasF2PVariant && (
            <>
              {marketingCheckBox}
              <AgeCertificationCheckbox
                overEighteenCertification={overEighteenField.input.value}
                setOverEighteenCertification={overEighteenField.handlers.setValue}
                setError={setError}
                isFullScreen={isFullScreen}
                shouldShowAgeCertification={shouldShowAgeCertification}
                isInvalid={ageCertificationInvalid}
                setAgeCertificationInvalid={setAgeCertificationInvalid}
              />
              {termsAndConditions && (
                <FormSection className="termsandconditions">
                  <Paragraph format="soft" size="3">
                    {termsAndConditions}
                  </Paragraph>
                  {disclaimer && <Paragraph size="3">{disclaimer}</Paragraph>}
                </FormSection>
              )}
            </>
          )}
        </>
      )}
      <section>
        <ContinueButton
          disabled={!hasF2PVariant && !emailForm.valid}
          loading={emailForm.submitting}
          onClick={() => {
            trackFinishAuthFlow({ ...bpData });
            trackRegistration('password', fa);
            if (isFullScreen) {
              trackJoinPageClickRegFlow0625({
                location: 'join_page_step_2',
                event_name: 'join_with_email',
                copy: 'join_with_email',
                target: 'email_auth_join',
                password_strength_score: passwordScore,
              });
            } else trackJoinWithEmailClick(Boolean(hideEmail), redirectUrl);
          }}
          pill={pill}
          format={hasF2PVariant ? 'basic' : undefined}
          className={className}
          data-testid="join-continue-button"
        >
          {submitCtaText}
        </ContinueButton>
      </section>
    </Form>
  );
}
